import React, { useEffect, useState } from 'react'
import {
  Col,
  Modal,
  notification,
  Row,
  Spin,
  Tabs,
  Timeline,
  Tooltip,
  Typography
} from 'antd'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { GetRequestHistory } from 'src/services/api/requests'
import {
  Id,
  OccurrenceType,
  RequestApprovalStatus,
  RequestHistory,
  RequestHistoryOccurrence,
  RequestUserDataStatus
} from 'src/types'
import UserAvatar from 'src/components/UserAvatar'

import * as CSS from './styles'

interface LastTimelineItemProps {
  status: RequestApprovalStatus | RequestUserDataStatus
  t: (value: string) => string
}

interface TimelineItemProps {
  occurrence: RequestHistoryOccurrence
  t: (value: string) => string
}

function setColorItem(status: string): string {
  if (!status) {
    return 'gray'
  }

  switch (status.toLowerCase()) {
    case 'approval_reproved':
    case 'closing_reproved':
    case 'conference_reproved':
    case 'reproved':
      return 'red'
    case 'approval_pending':
    case 'closing_pending':
    case 'conference_pending':
    case 'pending':
      return 'blue'
    case 'approved':
    case 'finished':
    case 'underway':
      return 'green'
    case 'expired':
    case 'canceled':
    default:
      return 'gray'
  }
}

function LastTimelineItem({ status, t }: LastTimelineItemProps) {
  return (
    <Timeline.Item color={setColorItem(status)}>
      <Typography.Text strong>
        {translateOccurrenceStatus(t, status)}
      </Typography.Text>
    </Timeline.Item>
  )
}

function translateOccurrenceType(
  t: (value: string) => string,
  type: OccurrenceType
) {
  /**
   * web.user.common.role.approval
   * web.user.common.role.conference
   * web.user.common.role.closing
   * web.user.common.role.emission
   * */
  switch (type) {
    case 'APPROVAL':
      return t('web.user.common.role.approval')
    case 'CONFERENCE':
      return t('web.user.common.role.conference')
    case 'CLOSING':
      return t('web.user.common.role.closing')
    case 'EMISSION':
      return t('web.user.common.role.emission')
    default:
      return <></>
  }
}

/**
 *
 * @param status
 * @returns translate
 */
function translateOccurrenceStatus(
  t: (value: string) => string,
  status: RequestApprovalStatus | RequestUserDataStatus
) {
  /**
   * common.status.unified.approved
   * common.status.unified.canceled
   * common.status.unified.expired
   * common.status.unified.pending
   * common.status.unified.reproved
   * common.status.unified.approval_pending
   * common.status.unified.approval_reproved
   * common.status.unified.canceled
   * common.status.unified.underway
   * common.status.unified.conference_pending
   * common.status.unified.conference_reproved
   * common.status.unified.closing_pending
   * common.status.unified.closing_reproved
   * common.status.unified.finished
   */
  if (!status) {
    return <></>
  }

  return t(`common.status.unified.${status}`.toLowerCase())
}

function TimelineItem({ occurrence, t }: TimelineItemProps) {
  const created_on = moment(occurrence.created_on * 1000).format(
    'DD/MM/YYYY HH:mm'
  )
  const occurrence_type = translateOccurrenceType(
    t,
    occurrence?.occurrence_type
  )
  const status = translateOccurrenceStatus(t, occurrence.status)

  return (
    <Timeline.Item color={setColorItem(occurrence.status)}>
      <Row>
        <Col span={4}>
          <Typography.Text strong>{occurrence_type}</Typography.Text>
          <br />
          {occurrence.responsible?.name && (
            <Tooltip title={occurrence.responsible.name}>
              <UserAvatar
                gender={occurrence.responsible.gender}
                name={occurrence.responsible.name}
                size="large"
              />
            </Tooltip>
          )}
        </Col>
        <Col span={6}>
          <Typography.Text>{created_on}</Typography.Text>
          <br />
          <CSS.Occurrence strong>{status}</CSS.Occurrence>
        </Col>
        <Col span={14}>
          {!!occurrence?.justification && (
            <div>
              <CSS.Justification>{occurrence.justification}</CSS.Justification>
            </div>
          )}
        </Col>
      </Row>
    </Timeline.Item>
  )
}

interface RequestHistoryButtonModalProps {
  modalVisible: boolean
  request_id: Id
  toggle: () => void
  user_id: Id
}

export default function RequestHistoryButtonModal({
  modalVisible,
  request_id,
  toggle,
  user_id
}: RequestHistoryButtonModalProps) {
  const [dataSource, setDateSource] = useState<RequestHistory[]>([])
  const [isLoading, setIsLoading] = useState<boolean>()

  const { t } = useTranslation()

  useEffect(() => {
    let isSubscribed = true
    if (!dataSource.length && modalVisible) {
      setIsLoading(true)
      GetRequestHistory({
        request_id,
        user_id
      })
        .then(response => {
          if (isSubscribed) {
            if (response.status === 200) {
              setDateSource(response.data.request_history || [])
              return
            }
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        })
        .catch(response => {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    return () => {
      isSubscribed = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalVisible, request_id, user_id])

  return (
    <Modal
      cancelButtonProps={{ hidden: true }}
      destroyOnClose
      okText={t('common.actions.close')}
      onCancel={() => {
        toggle()
      }}
      onOk={() => {
        toggle()
      }}
      title={t('web.request.history.modal-title')}
      visible={modalVisible}
      width={820}
    >
      {isLoading ? (
        <Spin />
      ) : (
        dataSource.length > 0 && (
          <Tabs defaultActiveKey="0">
            {dataSource.map((item, index) => (
              <Tabs.TabPane
                key={`${index}`}
                tab={
                  <Tooltip title={item.traveler.name}>
                    <UserAvatar
                      gender={item.traveler.gender}
                      name={item.traveler.name}
                      size="large"
                    />
                  </Tooltip>
                }
              >
                <CSS.Timeline>
                  {item.occurrences.map(occurrence => (
                    <TimelineItem
                      key={occurrence.created_on}
                      occurrence={occurrence}
                      t={t}
                    />
                  ))}
                  <LastTimelineItem status={item.status} t={t} />
                </CSS.Timeline>
              </Tabs.TabPane>
            ))}
          </Tabs>
        )
      )}
    </Modal>
  )
}
